import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorInfo: string;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    errorMessage: "",
    errorInfo: "",
  };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      errorMessage:
        error instanceof Error ? error.message : "An unexpected error occurred.",
      errorInfo: "",
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("[APP_ERROR_BOUNDARY_CAUGHT]", error, info.componentStack);
    this.setState({
      errorInfo: info.componentStack || "",
    });
  }

  private retry = () => {
    this.setState({
      hasError: false,
      errorMessage: "",
      errorInfo: "",
    });

    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <main
          role="alert"
          className="app-error-page min-h-screen flex flex-col items-center justify-center bg-slate-900 text-slate-100 p-6 text-center"
        >
          <div className="max-w-md w-full bg-slate-800/90 border border-slate-700/80 rounded-xl p-8 shadow-2xl backdrop-blur-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
              !
            </div>
            <h1 className="text-2xl font-bold text-slate-100 mb-2">Something went wrong</h1>
            <p className="text-slate-300 text-sm mb-6 leading-relaxed">
              BizNearYou could not finish loading this page. Your account data and saved progress remain safe and unaffected.
            </p>

            {(import.meta as any).env?.DEV && this.state.errorMessage && (
              <div className="text-left bg-slate-950 p-3 rounded-lg text-xs font-mono text-amber-300 mb-6 overflow-x-auto max-h-40 border border-slate-800">
                <p className="font-semibold mb-1 text-red-400">Error Details:</p>
                <p>{this.state.errorMessage}</p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={this.retry}
                className="px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-sm transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                Reload Page
              </button>
              <a
                href="/"
                className="px-5 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium text-sm transition-colors"
              >
                Return Home
              </a>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
